import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import ExperienceCallout from './components/experienceCallout';
import Faq from './components/faq';
import FinancialPlanningSection from './components/financialPlanningSection';
import HeroSection from './components/heroSection';
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import IntroSection from './components/introSection/introSection';
import Metadata from './components/metadata';
import QuizCallout from './components/quizCallout';
import RelatedLinksSection from './components/relatedLinksSection';
import UnderstandingProduct from './components/understandingProduct';
// images
import highResDesktopImage from './components/heroSection/images/variable-universal-life-insurance-hero.webp';
import highResMobileImage from './components/heroSection/images/variable-universal-life-insurance-hero-mobile.webp';
// dislaimer footnotes
import {
  HighestFinancialStrengthRatings,
  LoyaltyBasedOnClientData,
  NerdWalletAllRightsReserved,
} from '../../../utils/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      disableDisclaimerTopBorder
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Insurance',
          linkUrl: '/insurance/',
          linkAriaLabel: 'Northwestern Mutual Insurance',
        },
        {
          pageName: 'Life Insurance',
          linkUrl: '/life-insurance/',
          linkAriaLabel: 'Northwestern Mutual Life Insurance',
        },
        {
          pageName: 'Variable Universal Life Insurance',
          linkUrl: '/life-insurance/variable-universal-life-insurance-vul/',
          linkAriaLabel: 'Northwestern Mutual Variable Universal Life Insurance',
        },
      ]}
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;Your policy&#39;s cash value typically becomes a useful source of funds only after you have built up your cash value. Cash value is depending on the time and amount of the premiums paid and market performance. Each method of utilizing your policy&#39;s cash value has advantages and disadvantages and is subject to different tax consequences. Withdrawals from and loans against a policy will reduce the policy&#39;s cash surrender value and death benefit. As a general rule, withdrawals are taxable to the extent they exceed the cost basis of the policy, while loans are not taxable when taken. Loans taken against a life insurance policy can have adverse effects if not managed properly. Policy loans, including any accrued interest, must be repaid in cash or from policy values upon policy termination or the death of the insured. Repayment of loans from policy values (other than death proceeds) can potentially trigger a significant tax liability, and there may be little or no cash value remaining in the policy to pay the tax. If loans equal or exceed the cash value, the policy will terminate if additional cash payments are not made. Policyowners should consult with their tax advisors about the potential impact of any withdrawals or loans.
        </>,
        <>
          <sup>2</sup>&nbsp;The principal and interest credited to amounts in the SAS Account are guaranteed by the Company and are subject to the claims&#45;paying ability of the Company. There are restrictions on the timing and amounts that can be allocated or transferred to the SAS Account. Amounts in the SAS Account may not be transferred from the SAS Account to the Divisions. You should carefully consider whether the SAS Account is suitable for you in light of your investment objectives and liquidity needs. Review the{' '}
          <Link
            linkUrl='https://dfinview.com/NorthwesternMutual/TADF/DFIN11168/P'
            className='nmx-link'
            variant='primaryDeemphasize'
            isOutgoingLink>
            Variable Universal Life Plus (VULP) Prospectus
          </Link>
          {' '}for full details and limitations of the SAS account. Consult your Financial Representative with any questions.
        </>,
        <>
          <sup>3</sup>&nbsp;Policyowners are not investing in the general account portfolio when purchasing universal life insurance. They&#39;re buying products backed by the financial strength of Northwestern Mutual. The policy guarantees are backed by the financial strength and the claims paying ability of Northwestern Mutual.
        </>,
        <>
          <sup>4</sup>&nbsp;It&#39;s important to remember that all investments carry some level of risk, including the potential loss of principal invested. Dollar Cost Averaging is an investment strategy; no investment strategy can guarantee a profit or protect against a loss.
        </>,
        <>
          {HighestFinancialStrengthRatings(5)}
        </>,
        <>
          {LoyaltyBasedOnClientData(6)}
        </>,
        <>
          {NerdWalletAllRightsReserved(7)}
        </>,
      ]}
      customDisclaimers={[
        <>
          Variable contracts have limitations. You should carefully consider the investment objectives, risks, expenses and charges of the investment company before you invest. A registered representative can provide you with a policy prospectus as well as prospectuses for the funds that will contain the information noted above and other important information that you should read carefully before you invest or send money.
        </>,
        <>
          Cash values are not guaranteed with variable life.
        </>,
        <>
          State variations may apply. All products may not be available in all states.
        </>,
        <>
          Issuer: The Northwestern Mutual Life Insurance Company, Milwaukee, WI.
        </>,
        <>
          Principal Underwriter: <strong>Northwestern Mutual Investment Services, LLC</strong> (NMIS) (securities), subsidiary of NM, registered investment adviser, broker&#45;dealer, member FINRA and SIPC.
        </>,
        <>
          To be used with form numbers UU.VULP.(1019), UU.EVUL.(1019), UU.VULP.APB.(1019), UU.EVUL.SOPE.(1019) or state equivalent. Not all contracts and optional benefits are available in all states.
        </>,
      ]}
      stateFilingComplianceNumber={<>90&#45;2758 (0322)</>}
    >
      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResDesktopImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResMobileImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImage}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      <UnderstandingProduct />

      <QuizCallout />

      <Faq />

      {/* In page lead form (FAFA) */}
      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id="in-page-lead-form" will be on the Form element itself.
          config={config}
          loadRecaptcha={loadRecaptcha}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          formLocation='variable-universal-life-insurance-vul'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          formVariant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      <FinancialPlanningSection />

      <ExperienceCallout />

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId='content-hub'
        headingText='Boost your variable universal life insurance knowledge'
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

      <RelatedLinksSection />

    </Page>
  );
};
